import React, { useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faStar,
  faRotateLeft,
  faExpand,
  faCompress,
} from '@fortawesome/free-solid-svg-icons';
import ChatBubble from './ChatBubble';
import ChatInput from './ChatInput';
import type { ChatMessage, MessageRatingValue } from '../types';

interface ChatPanelProps {
  sessionId: string;
  messages: ChatMessage[];
  isLoading: boolean;
  isSessionLimitReached?: boolean;
  onSend: (text: string) => void;
  onReset: () => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  seenMessageIds: Set<string>;
  onQueryClick?: (url: string) => void;
  onFacetClick?: (label: string) => void;
  onSuggestionClick?: (text: string) => void;
  placeholder?: string;
  headerTitle?: string;
  headerSubtitle?: string;
  greetingMessage?: string;
  projectInfoUrl?: string;
  projectInfoLinkText?: string;
  showDebugInfo?: boolean;
  showSessionId?: boolean;
  feedbackEnabled?: boolean;
  feedbackStyle?: 'stars' | 'thumbs';
  feedbackTagsPositive?: string[];
  feedbackTagsNegative?: string[];
  onSubmitFeedback?: (
    messageId: string,
    value: MessageRatingValue,
    comment: string,
    tags: string[],
  ) => Promise<void>;
}

/**
 * The inner chat panel — renders the message list, input bar, and header.
 * All state (messages, loading) is owned by the parent so conversations
 * persist even when the modal is closed and reopened.
 */
const ChatPanel: React.FC<ChatPanelProps> = ({
  sessionId,
  messages,
  isLoading,
  isSessionLimitReached = false,
  onSend,
  onReset,
  isExpanded,
  onToggleExpand,
  seenMessageIds,
  onQueryClick,
  onFacetClick,
  onSuggestionClick,
  placeholder,
  headerTitle = 'Enhanced Search',
  headerSubtitle = 'Natural language to advanced search queries',
  greetingMessage,
  projectInfoUrl,
  projectInfoLinkText = 'Learn more about this project',
  showDebugInfo,
  showSessionId = true,
  feedbackEnabled = false,
  feedbackStyle = 'stars',
  feedbackTagsPositive = [],
  feedbackTagsNegative = [],
  onSubmitFeedback,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasStreamingAssistantMessage = messages.some(
    (msg) => msg.role === 'assistant' && (msg.isStreaming ?? false),
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div className="ves-chat-panel">
      {/* Header */}
      <div className="ves-chat-header">
        <div className="ves-chat-header-icon">
          <FontAwesomeIcon icon={faSearch} />
        </div>
        <div className="ves-chat-header-info">
          <h1 className="ves-chat-header-title">
            {headerTitle}
            <FontAwesomeIcon icon={faStar} className="ves-sparkle-icon" />
          </h1>
          <p className="ves-chat-header-subtitle">{headerSubtitle}</p>
        </div>

        {/* Header actions */}
        <div className="ves-chat-header-actions">
          <button
            onClick={onReset}
            className="ves-header-button"
            title="Start a new conversation"
          >
            <FontAwesomeIcon icon={faRotateLeft} />
            <span>New Chat</span>
          </button>

          {/* Expand / Collapse toggle */}
          {onToggleExpand && (
            <button
              onClick={onToggleExpand}
              className="ves-header-button ves-header-button--icon"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              title={isExpanded ? 'Compact view' : 'Expand for larger view'}
            >
              <FontAwesomeIcon icon={isExpanded ? faCompress : faExpand} />
            </button>
          )}
        </div>
      </div>

      {showSessionId && (
        <div className="ves-chat-session-id" title={sessionId}>
          <span>Session ID</span>
          <code>{sessionId}</code>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="ves-chat-messages">
        {messages.length === 0 && (
          <div className="ves-chat-empty">
            <div className="ves-chat-empty-icon">
              <FontAwesomeIcon icon={faSearch} />
            </div>
            {greetingMessage && greetingMessage.trim() !== '' ? (
              <p className="ves-chat-greeting">{greetingMessage}</p>
            ) : (
              <div>
                <p className="ves-chat-empty-title">
                  Ask me anything to search
                </p>
                <p className="ves-chat-empty-hint">
                  Try: "Colorado lakes from 2024"
                </p>
              </div>
            )}
            {projectInfoUrl && projectInfoUrl.trim() !== '' && (
              <a
                className="ves-chat-greeting-link"
                href={projectInfoUrl}
                {...(/^https?:\/\//i.test(projectInfoUrl)
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
              >
                {projectInfoLinkText}
              </a>
            )}
          </div>
        )}

        {messages.map((msg) => {
          const alreadySeen = seenMessageIds.has(msg.id);
          // Mark this message as seen for future reopens
          seenMessageIds.add(msg.id);
          return (
            <ChatBubble
              key={msg.id}
              message={msg}
              skipAnimation={alreadySeen}
              onQueryClick={onQueryClick}
              onFacetClick={onFacetClick}
              onSuggestionClick={onSuggestionClick}
              showDebugInfo={showDebugInfo}
              feedbackEnabled={feedbackEnabled}
              feedbackStyle={feedbackStyle}
              feedbackTagsPositive={feedbackTagsPositive}
              feedbackTagsNegative={feedbackTagsNegative}
              onSubmitFeedback={onSubmitFeedback}
            />
          );
        })}

        {isLoading && !hasStreamingAssistantMessage && (
          <div className="ves-chat-bubble ves-chat-bubble--agent">
            <div className="ves-avatar ves-avatar--agent">
              <FontAwesomeIcon icon={faSearch} />
            </div>
            <div className="ves-bubble-content">
              <div className="ves-role-label ves-role-label--agent">Agent</div>
              <div className="ves-message ves-message--agent ves-loading-dots">
                <span className="ves-dot" />
                <span className="ves-dot" />
                <span className="ves-dot" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <ChatInput
        onSend={onSend}
        disabled={isLoading || isSessionLimitReached}
        disabledPlaceholder={
          isSessionLimitReached
            ? 'This conversation has reached its usage limit.'
            : undefined
        }
        placeholder={placeholder}
      />
    </div>
  );
};

export default ChatPanel;
